import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InfoTip from "../wizard/components/InfoTip";
import { normalizeHex } from "../../../../utils/color";

/**
 * The building blocks every Site & widget panel is made of, so the five of them
 * cannot drift apart: a card with a title and a line under it, labelled fields
 * with an optional (i) tip, and the colour field.
 */

export const labelTextClass = "text-sm font-semibold text-ink-800";

export const PanelCard = ({ title, subtitle = null, children }) => (
  <div className="min-w-0 rounded-card border border-ink-200 bg-white p-4 shadow-card sm:p-5">
    <h2 className="m-0 text-lg font-semibold text-ink-900">{title}</h2>
    {subtitle && <p className="mt-0.5 mb-0 text-sm text-ink-500">{subtitle}</p>}
    <div className="mt-5 flex flex-col gap-5">{children}</div>
  </div>
);

PanelCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node,
};

export const Field = ({ id, label, optional = false, info = null, hint = null, children }) => {
  const { t } = useTranslation();

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5">
        <label htmlFor={id} className={labelTextClass}>
          {label}
        </label>
        {optional && (
          <span className="text-sm text-ink-400">({t("Dashboard.MySite.optional")})</span>
        )}
        {info && (
          <InfoTip label={t("Dashboard.Setup.moreAbout", { label })} text={info} />
        )}
      </div>
      {children}
      {hint && <p className="mt-1.5 mb-0 text-xs text-ink-500">{hint}</p>}
    </div>
  );
};

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  optional: PropTypes.bool,
  info: PropTypes.string,
  hint: PropTypes.string,
  children: PropTypes.node,
};

/** A line of help at the foot of a panel, with its icon. */
export const PanelNote = ({ icon = "bi-info-circle", children }) => (
  <p className="m-0 flex items-start gap-2 text-xs leading-relaxed text-ink-500">
    <i className={`bi ${icon} mt-px text-sm`} aria-hidden="true" />
    <span className="min-w-0">{children}</span>
  </p>
);

PanelNote.propTypes = {
  icon: PropTypes.string,
  children: PropTypes.node,
};

const hexInputClass =
  "w-28 min-w-0 rounded-lg border bg-white px-3 py-2.5 font-mono text-sm text-ink-900 transition focus:outline-none focus:ring-2";

/**
 * A swatch and its hex value, bound to one form field. The swatch is the quick
 * way, the text the exact one; a value that is not a colour yet (half-typed)
 * says so instead of quietly falling back on the site.
 */
export const ColorField = ({ id, label, hint = null, value, onChange }) => {
  const { t } = useTranslation();
  const valid = Boolean(normalizeHex(value));

  return (
    <div className="min-w-0">
      <label htmlFor={`${id}-text`} className={`${labelTextClass} mb-1.5 block`}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={id}
          name={id}
          value={normalizeHex(value) || "#000000"}
          onChange={onChange}
          aria-label={t("Dashboard.Wizard.Steps.bookingPage.themeSection.colorPickerAriaLabel", { label })}
          className="size-11 shrink-0 cursor-pointer rounded-lg border border-ink-200 bg-white p-1"
        />
        <input
          type="text"
          id={`${id}-text`}
          name={id}
          value={value}
          onChange={onChange}
          maxLength={7}
          pattern="#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})"
          title={t("Dashboard.MySite.brand.invalidColor")}
          spellCheck={false}
          aria-invalid={!valid}
          aria-describedby={valid ? undefined : `${id}-error`}
          className={`${hexInputClass} ${
            valid
              ? "border-ink-200 focus:border-brand-600 focus:ring-brand-600/20"
              : "border-red-400 focus:border-red-500 focus:ring-red-500/20"
          }`}
        />
      </div>
      {!valid && (
        <p id={`${id}-error`} className="mt-1.5 mb-0 text-xs text-red-600">
          {t("Dashboard.MySite.brand.invalidColor")}
        </p>
      )}
      {hint && valid && <p className="mt-1.5 mb-0 text-xs text-ink-500">{hint}</p>}
    </div>
  );
};

ColorField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
